// theirs
import { isMobileOnly } from 'react-device-detect';
import { polyfill } from 'mobile-drag-drop';
import { scrollBehaviourDragImageTranslateOverride } from 'mobile-drag-drop/scroll-behaviour';
import React, { Component, PropsWithChildren, ReactComponentElement } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
// ours
import MixpanelService from './services/mixpanel.service';
import Board from 'src/bw/board/Board';
import BudgetJourney from 'src/components/budgets/budget-journey/BudgetJourney';
import CobrandService from './services/cobrand.service';
import { initializePageLoading } from './services/pageLoading.service';
import { initializePageAlert } from './services/pageAlert.service';
import ErrorBoundaryService from './services/error-boundary.service';
import LiteralsService from './services/literals/LiteralsService';
import store from 'src/bw/redux/store';
import Typography from './components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import { history } from 'src/services/router.service';
// styles and icons
import { ReactComponent as LogoIcon } from 'src/assets/svg/logo.svg';
import 'mobile-drag-drop/default.css';
import './App.scss';
import { QueryClient, QueryClientProvider, QueryErrorResetBoundary } from '@tanstack/react-query';
import PageLoading from './components/atomic/atoms/pageLoading/PageLoading';
import PageAlert from './components/atomic/atoms/pageAlert/PageAlert';
import { ALLOW_TEST_LOGIN, ENABLE_ADMIN } from 'src/config';
import UrlHelper from 'src/utils/url.helper';
import ErrorView from 'src/components/atomic/pages/error/ErrorView';
import LoginForm from 'src/components/login-form/LoginForm';
import { RouteProps } from 'react-router';
import BudgetingAuthWrapper from 'src/components/EligibilityCheck/BudgetingAuthWrapper';
import { BudgetPages } from 'src/components/atomic/pages/BudgetPages';
import ErrorBoundary from 'src/components/atomic/atoms/errorBoundary/ErrorBoundary';
import { retryError } from 'src/utils/error.utils';
import ScriptSupplierService from './services/script-supplier.service';
import AdminPage from 'src/components/admin/AdminPage';
import DynatraceService from './services/dynatrace.service';
import { OneXPContext, sdkClient } from './utils/onexp.utils';
import { isMobileApp } from 'src/utils/url.utils';

// Shim for older browsers (FF)
/* eslint no-extend-native: off */
if (!String.prototype.replaceAll) {
    String.prototype.replaceAll = function (find: string | RegExp, replace: any) {
        const str = this;
        return str.replace(new RegExp(find, 'g'), replace);
    };
}

class App extends Component<any> {
    public override state = {
        isSmallScreen: true,
        alert: undefined as JSX.Element,
        loading: false
    };

    constructor(props: any) {
        super(props);
        polyfill({
            dragImageTranslateOverride: scrollBehaviourDragImageTranslateOverride
        });
        void MixpanelService.init().then();
    }

    public override componentDidMount(): void {
        CobrandService.setCobrandFromLocation(window.location);
        initializePageAlert((alert: JSX.Element) => {
            this.setState({ ...this.state, alert });
        });
        initializePageLoading((loading: boolean) => {
            this.setState({ ...this.state, loading });
        });
        // Load AXP Script Supplier
        ScriptSupplierService.loadSSToPage();
        // Append Dynatrace RUM script tag if not present and if the environment variable is set
        DynatraceService.appendDynatraceScript();
        // Reload the page to clear the cache. Useful in iOS where we have seen back forward cache (bfcache) issues
        // and javascript not being executed on navigating back to a page.
        // (https://stackoverflow.com/questions/11979156/mobile-safari-back-button#comment36873323_12652160)
        window.addEventListener('pageshow', (event: PageTransitionEvent) => {
            // event.persisted is true when the page is loaded from the bfcache
            if (event.persisted) {
                window.location.reload();
            }
        });
    }

    public override componentWillUnmount(): void {
        window.removeEventListener('pageshow', () => {});
    }

    // If a fatal error happens anywhere in the app: log, report, recover.
    public override componentDidCatch(error: any, info: any): void {
        ErrorBoundaryService.recover(error, info).catch(console.error);
    }

    public override render(): ReactComponentElement<any> {
        if (isMobileOnly && !process.env.REACT_APP_MOBILEON) {
            return (
                <div className="mobile-screen" key="mobile-screen">
                    <div className="mobile-text">
                        <LogoIcon />
                        <Typography variant={TypographyVariants.HEADING_XS}>
                            {LiteralsService.getAppName()} mobile coming soon.
                        </Typography>
                        <Typography variant={TypographyVariants.BODY}>
                            Until then, try {LiteralsService.getAppName()} on your laptop, desktop, or tablet.
                        </Typography>
                    </div>
                </div>
            );
        } else {
            return (
                <QueryClientProviderWrapper location={window.location}>
                    <OneXPContext.Provider value={sdkClient}>
                        <Provider store={store}>
                            <Router history={history}>
                                <Switch>
                                    {ENABLE_ADMIN && (
                                        <Route path={UrlHelper.getFullPath('admin')} component={AdminPage} />
                                    )}
                                    {ALLOW_TEST_LOGIN && (
                                        <Route path={UrlHelper.getFullPath('login')} component={LoginForm} />
                                    )}
                                    <PrivateRoute path={UrlHelper.getFullPath('spendsmart')}>
                                        <BudgetJourney />
                                    </PrivateRoute>
                                    <Route exact path={UrlHelper.getFullPath('error')} component={ErrorView} />
                                    <Route
                                        render={(props: any) => {
                                            const pathname: string = UrlHelper.stripBasePath(window.location.pathname);
                                            return (
                                                <Board
                                                    {...props}
                                                    subRoute={pathname}
                                                    viewId={Number(pathname.split('/').pop())}
                                                />
                                            );
                                        }}
                                    />
                                </Switch>
                            </Router>
                            {this.state.alert && <PageAlert>{this.state.alert}</PageAlert>}
                            {this.state.loading && <PageLoading />}
                        </Provider>
                    </OneXPContext.Provider>
                </QueryClientProviderWrapper>
            );
        }
    }
}

const QueryClientProviderWrapper = ({
    location,
    children
}: PropsWithChildren<{ location?: Location }>): JSX.Element => {
    const queryClient: QueryClient = new QueryClient({
        defaultOptions: {
            queries: {
                staleTime: 5 * (60 * 1000), // 5 minutes
                cacheTime: 10 * (60 * 1000), // 10 minutes
                useErrorBoundary: true,
                retry: (failureCount, error) => retryError(error, isMobileApp()) && failureCount < 3
            },
            mutations: {
                useErrorBoundary: true
            }
        }
    });
    return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};

const PrivateRoute = ({ children, location, ...rest }: RouteProps): JSX.Element => {
    const requiresAuth = location.pathname !== BudgetPages.INELIGIBLE;
    return (
        <Route {...rest}>
            <QueryErrorResetBoundary>
                {({ reset }) => (
                    <ErrorBoundary reset={reset} isMobileApp={isMobileApp()}>
                        {requiresAuth ? <BudgetingAuthWrapper>{children}</BudgetingAuthWrapper> : children}
                    </ErrorBoundary>
                )}
            </QueryErrorResetBoundary>
        </Route>
    );
};

export default App;
